import EventEmitter from 'eventemitter3';
import Color from '../data/Color';
import ColorsPaletteEvents from '../events/ColorsPaletteEvents';
import { DisplayObject, getMovieClipByName, getNominalBounds, MouseEvent, Stage, toProperCase } from '../utils/utils';

// ==================================================================================================
export interface IColorData {
  color: Color;
  name: string;
}

export default class ColorsPalette {
  // ================================================================================================
  // properties
  // ================================================================================================
  private colorsByClip: Map<DisplayObject, IColorData>;

  private stage: Stage;

  private _events: EventEmitter;

  // ================================================================================================
  // public
  // ================================================================================================

  // ----------------------------------------------------------------------------------------------
  constructor(stage: Stage) {
    this._events = new EventEmitter();
    this.stage = stage;
    this.initColors();
  }

  // ----------------------------------------------------------------------------------------------
  get events(): EventEmitter {
    return (this._events);
  }

  // ----------------------------------------------------------------------------------------------
  getColorByName(colorName: string): IColorData {
    const colorsData = Array.from(this.colorsByClip.values());
    return (colorsData.find((data) => data.name === colorName));
  }

  // ================================================================================================
  // private
  // ================================================================================================

  // ----------------------------------------------------------------------------------------------
  private getColorFromMouseEventData(eventObject: unknown): IColorData {
    const mouseEvent = <MouseEvent> eventObject;
    const colorDisplay = <DisplayObject> mouseEvent?.target;
    return (this.colorsByClip.get(colorDisplay));
  }

  // ----------------------------------------------------------------------------------------------
  private static GetColorName(colorDisplay: createjs.DisplayObject): string {
    const name = colorDisplay.name;
    const parts = name.split('_');
    if (parts[0] !== 'btn') {
      console.warn(`invalid color button name: ${name}`);
      return (name);
    }

    parts.shift();

    for (let index = 0; index < parts.length; index += 1) {
      parts[index] = toProperCase(parts[index]);
    }

    return (parts.join(' '));
  }

  // ----------------------------------------------------------------------------------------------
  private initColor(colorDisplay: DisplayObject, context: CanvasRenderingContext2D): void {
    colorDisplay.cursor = 'pointer';
    colorDisplay.mouseEnabled = true;
    colorDisplay.addEventListener('click', this.onColorClick.bind(this));
    colorDisplay.addEventListener('mouseover', this.onColorMouseOver.bind(this));

    this.initColorMapping(colorDisplay, context);
  }

  // ----------------------------------------------------------------------------------------------
  private initColorMapping(colorDisplay: DisplayObject, context: CanvasRenderingContext2D): void {
    const bounds = getNominalBounds(colorDisplay);
    if (!bounds) {
      console.warn('Unable to get the nominal bounds in Color display object with name',
        colorDisplay.name);
      return;
    }

    colorDisplay.draw(context);

    const imageData = context.getImageData(0, 0, bounds.width, bounds.height);

    const color = new Color();
    color.red = imageData.data[0];
    color.green = imageData.data[1];
    color.blue = imageData.data[2];
    color.update();

    this.colorsByClip.set(colorDisplay, {
      color,
      name: ColorsPalette.GetColorName(colorDisplay),
    });
  }

  // ----------------------------------------------------------------------------------------------
  private initColors(): void {
    this.colorsByClip = new Map();

    const canvas = <HTMLCanvasElement> document.createElement('canvas');
    const context = canvas.getContext('2d');

    const COLORS_PALETTE_NAME = 'colors_palette';
    const colorsPalette = getMovieClipByName(this.stage, COLORS_PALETTE_NAME);
    if (!colorsPalette) {
      console.warn(`MovieClip with name ${COLORS_PALETTE_NAME} not found on the stage.`);
      return;
    }

    colorsPalette.children.forEach((colorDisplay) => {
      this.initColor(colorDisplay, context);
    });
  }

  // ----------------------------------------------------------------------------------------------
  private onColorClick(eventObject: unknown): void {
    const colorData = this.getColorFromMouseEventData(eventObject);
    if (colorData) {
      this._events.emit(ColorsPaletteEvents.COLOR_SELECTED, colorData, this);
    }
  }

  // ----------------------------------------------------------------------------------------------
  private onColorMouseOver(eventObject: unknown): void {
    const colorData = this.getColorFromMouseEventData(eventObject);
    if (colorData) {
      this._events.emit(ColorsPaletteEvents.COLOR_POINTER_OVER, colorData, this);
    }
  }
}
